import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface MaintenanceContextType {
  isMaintenanceMode: boolean;
  isAllowedUser: (email: string) => boolean;
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

interface MaintenanceProviderProps {
  children: ReactNode;
}

export function MaintenanceProvider({ children }: MaintenanceProviderProps) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean>(false);
  const [allowedEmails, setAllowedEmails] = useState<string[]>([]);

  useEffect(() => {
    // Read maintenance mode from environment variable
    const maintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';
    setIsMaintenanceMode(maintenanceMode);

    // Read the list of allowed emails from environment variable
    const allowedEmailsEnv = process.env.REACT_APP_MAINTENANCE_ALLOWED_EMAILS || '';

    const emailsList = allowedEmailsEnv
      .split(',')
      .map(email => email.trim().toLowerCase())
      .filter(email => email.length > 0);

    setAllowedEmails(emailsList);
  }, []);

  // Function to check if a user's email is in the allowed list
  const isAllowedUser = (email: string): boolean => {
    if (!email) {
      console.log('Maintenance check: No email provided, access denied');
      return false;
    }
    
    const normalizedEmail = email.toLowerCase();
    const isAllowed = allowedEmails.includes(normalizedEmail);
    
    console.log(`Maintenance check for email: ${normalizedEmail}`);
    console.log(`Is allowed: ${isAllowed ? 'YES' : 'NO'}`);
    
    if (!isAllowed && allowedEmails.length > 0) {
      console.log('Allowed emails for reference:');
      allowedEmails.forEach(allowedEmail => {
        console.log(` - ${allowedEmail}`);
      });
    }
    
    return isAllowed;
  };

  return (
    <MaintenanceContext.Provider
      value={{
        isMaintenanceMode,
        isAllowedUser
      }}
    >
      {children}
    </MaintenanceContext.Provider>
  );
}

export const useMaintenance = (): MaintenanceContextType => {
  const context = useContext(MaintenanceContext);
  
  if (context === undefined) {
    throw new Error('useMaintenance must be used within a MaintenanceProvider');
  }
  
  return context;
};